import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Button } from '@/components/buttons/Button';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';
import CloseButton from '@/components/UI/Buttons/CloseButton';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import { cdnImage } from '@/utils/image-utils';
import StorageManager from '@/shared/storageManager';
import { useRewardsWheel } from '@/hooks/useRewardsWheel';
import { envIsCanada, envIsMexico } from '@/constants/constants';
import { useFluidOpen } from '@/features/cashier/fluid/fluidPaymentsStore';
import useIsHomePage from '@/hooks/useIsHomePage';
import { BaseModalProps } from '@/features/modal/types';

export const RewardsWheelReminderModal = ({ closeModal }: BaseModalProps) => {
  const intl = useIntl();
  const navigate = useAbsoluteNavigate();
  const { pagePath: rewardsWheelPagePath } = useRewardsWheel();
  const openCashier = useFluidOpen();
  const imageName = envIsCanada() ? 'daily-deposit-boost.png' : 'rewards-wheel-hero.png';
  const isHomePage = useIsHomePage();

  const handleOnClose = () => {
    closeModal();
    StorageManager.setRewardsReelReminder();
    triggerGTag(GTagEvents.rewards_wheel_reminder_close);
  };

  const handleCtaClick = () => {
    StorageManager.setRewardsReelReminder();
    triggerGTag(GTagEvents.rewards_wheel_reminder_cta);
    closeModal();
    if (envIsMexico()) {
      // In Mexico we want to open the cashier instead of
      // navigating to the rewards wheel page
      openCashier();
    } else if (envIsCanada()) {
      if (!isHomePage) navigate('/');
      const bonusInfo = document.getElementById('bonus-info');
      if (bonusInfo !== null) {
        bonusInfo.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        });
      }
    } else {
      navigate(`/${rewardsWheelPagePath}`);
    }
  };

  useEffect(() => {
    triggerGTag(GTagEvents.rewards_wheel_reminder_shown);
    StorageManager.setRewardsReelReminder();
  }, []);

  return (
    <div
      data-cy="rewards-wheel-reminder-modal"
      className="relative rounded-xl overflow-hidden bg-rewardsWheelBg p-8"
    >
      <CloseButton light left onClick={handleOnClose} />
      <img
        src={cdnImage(`rewards-wheel/${imageName}`, {
          width: '350px',
          height: 'auto',
          dpr: '2',
        })}
        width="350px"
        alt={intl.formatMessage({ id: 'rewardReel.h1' })}
      />
      <div className="text-white max-w-sm flex flex-col items-center gap-4 mt-4">
        <div className="text-lg font-bold">
          {intl.formatMessage({ id: 'rewardsWheelReminder.title' })}
        </div>
        <div className="text-md">{intl.formatMessage({ id: 'rewardsWheelReminder.subtitle' })}</div>
        <Button
          data-cy="rewards-wheel-reminder-modal-button"
          onClickHandler={handleCtaClick}
          btnType="primary"
          text={intl.formatMessage({ id: 'rewardsWheelReminder.cta' })}
        />
      </div>
    </div>
  );
};
